'use client';

import { Check, ChevronDown, Search, X } from 'lucide-react';

import { useCallback, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
} from '@/components/ui/command';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

import { Tooltip } from '@/shared/components';

import { cn } from '@/lib/utils';

interface MultiSelectOption {
	label: string;
	value: string;
}

interface MultiSelectDropdownProps {
	options: MultiSelectOption[];
	selectedValues: string[];
	onSelectionChange: (selectedValues: string[]) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	className?: string;
	disabled?: boolean;
	maxHeight?: number;
	width?: string;
	dropdownSide?: 'top' | 'bottom';
	dropdownAlign?: 'start' | 'center' | 'end';
	maxTriggerDisplayOptions?: number;
	/** Label used in the "{n} label" overflow text. Defaults to "selected". */
	countLabel?: string;
	/** "outline" renders the default bordered button; "ghost" renders a minimal borderless trigger. */
	variant?: 'outline' | 'ghost';
}

export function MultiSelectDropdown({
	options,
	selectedValues,
	onSelectionChange,
	placeholder = 'Select options...',
	searchPlaceholder = 'Search...',
	className,
	disabled = false,
	maxHeight = 300,
	width = 'w-64',
	dropdownSide = 'bottom',
	dropdownAlign = 'start',
	maxTriggerDisplayOptions = 2,
	countLabel = 'selected',
	variant = 'outline',
}: MultiSelectDropdownProps) {
	const [open, setOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const scrollAreaRef = useRef<HTMLDivElement>(null);

	const filteredOptions = useMemo(
		() => options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
		[options, searchTerm]
	);

	const handleToggleOption = useCallback(
		(value: string) => {
			const isSelected = selectedValues.includes(value);
			if (isSelected) {
				onSelectionChange(selectedValues.filter((v) => v !== value));
			} else {
				onSelectionChange([...selectedValues, value]);
			}
		},
		[selectedValues, onSelectionChange]
	);

	// const handleClearAll = useCallback(() => {
	// 	onSelectionChange([]);
	// }, [onSelectionChange]);

	// const handleSelectAll = useCallback(() => {
	// 	onSelectionChange(filteredOptions.map((option) => option.value));
	// }, [onSelectionChange, filteredOptions]);

	const getDisplayText = () => {
		if (selectedValues.length === 0) return placeholder;
		if (selectedValues.length <= maxTriggerDisplayOptions) {
			const selectedOptions = options.filter((option) => selectedValues.includes(option.value));
			return selectedOptions.length > 1
				? selectedOptions.map((option) => option.label).join(', ')
				: selectedOptions[0]?.label || placeholder;
		}
		return `${selectedValues.length} ${countLabel}`;
	};

	return (
		<DropdownMenu
			open={open}
			onOpenChange={setOpen}
		>
			<DropdownMenuTrigger asChild>
				{variant === 'ghost' ? (
					<button
						disabled={disabled}
						className={cn(
							'flex max-w-full items-center gap-1 overflow-hidden truncate text-xs text-muted-foreground outline-none transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50',
							className
						)}
					>
						<span className="truncate">{getDisplayText()}</span>
						<ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
					</button>
				) : (
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						className={cn(
							'w-full justify-between text-left font-normal',
							!selectedValues.length && 'text-muted-foreground',
							className
						)}
						disabled={disabled}
					>
						<span className="truncate">{getDisplayText()}</span>
						<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className={cn('p-0', width)}
				align={dropdownAlign}
				side={dropdownSide}
			>
				<Command className="w-full">
					<div className="flex flex-col p-1 pb-0">
						{/* Search Input */}
						<div className="relative mb-1">
							<Input
								type="text"
								placeholder={searchPlaceholder}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="h-9 px-9"
							/>
							<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 transform text-secondary" />

							{searchTerm && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="absolute right-0 top-1/2 -translate-y-1/2 transform hover:bg-transparent"
									onClick={() => setSearchTerm('')}
								>
									<X className="h-4 w-4" />
									<span className="sr-only">Clear search</span>
								</Button>
							)}
						</div>
					</div>
					<CommandList>
						<ScrollArea
							ref={scrollAreaRef}
							className="p-1 pt-0"
							style={{
								height: filteredOptions.length > 8 ? `${maxHeight}px` : 'auto',
							}}
						>
							<CommandEmpty>No matches</CommandEmpty>
							<CommandGroup>
								{filteredOptions.map((option) => {
									const isSelected = selectedValues.includes(option.value);
									const isOverflowingLabel = option?.label?.length * 6.6 > 200;

									return (
										<CommandItem
											key={option.value}
											onSelect={() => handleToggleOption(option.value)}
											className="w-full cursor-pointer"
										>
											<div className="flex items-center gap-2">
												<div className="flex size-4 items-center justify-center">
													{isSelected && <Check className="size-4" />}
												</div>
												<Tooltip
													key={option.value}
													text={isOverflowingLabel ? option.label : undefined}
													sideOffset={20}
													side="right"
													className="max-w-[250px]"
												>
													<span className="max-w-[200px] flex-1 truncate text-sm">
														{option.label}
													</span>
												</Tooltip>
											</div>
										</CommandItem>
									);
								})}
							</CommandGroup>
						</ScrollArea>
					</CommandList>
				</Command>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
